/**
 * Google Places API Integration
 *
 * Uses Google Places API (New) for business/POI search.
 * More comprehensive POI database than Mapbox Search Box API.
 *
 * Uses VITE_GOOGLE_PLACES_API_KEY configuration.
 */

import { env } from '@/config/environment';
import { GeocodedResult, AddressType } from './mapboxGeocoding';

// =============================================================================
// Types
// =============================================================================

interface GooglePlacesOptions {
  limit?: number;
  proximity?: { lat: number; lng: number };
}

interface GooglePlace {
  displayName: {
    text: string;
    languageCode: string;
  };
  formattedAddress: string;
  location: {
    latitude: number;
    longitude: number;
  };
  types?: string[];
  internationalPhoneNumber?: string;
  addressComponents?: Array<{
    types: string[];
    longText: string;
    shortText: string;
  }>;
}

interface GooglePlacesResponse {
  places?: GooglePlace[];
}

// =============================================================================
// Address Parsing
// =============================================================================

/**
 * Parse a formatted address string into components.
 * Google Places returns "123 Main St, Tampa, FL 33601, USA" format.
 */
function parseFormattedAddress(address: string): GeocodedResult['addressComponents'] {
  const parts = address.split(',').map(p => p.trim());

  // Typical format: "Street, City, State ZIP, Country"
  const components: GeocodedResult['addressComponents'] = {};

  if (parts.length >= 1) {
    // First part is usually street address
    const streetMatch = parts[0].match(/^(\d+)\s+(.+)$/);
    if (streetMatch) {
      components.houseNumber = streetMatch[1];
      components.road = streetMatch[2];
    } else {
      components.road = parts[0];
    }
  }

  if (parts.length >= 2) {
    components.city = parts[1];
  }

  if (parts.length >= 3) {
    // State and ZIP are often combined: "FL 33601"
    const stateZipMatch = parts[2].match(/^([A-Z]{2})\s*(\d{5}(?:-\d{4})?)$/);
    if (stateZipMatch) {
      components.state = stateZipMatch[1];
      components.postcode = stateZipMatch[2];
    } else {
      components.state = parts[2];
    }
  }

  return components;
}

// =============================================================================
// Response Mapping
// =============================================================================

function mapGooglePlaceToResult(place: GooglePlace): GeocodedResult {
  // Use API's addressComponents if available, otherwise fall back to parsing
  let addressComponents: GeocodedResult['addressComponents'];

  if (place.addressComponents && place.addressComponents.length > 0) {
    // Use structured addressComponents from API for reliable extraction
    const findComponent = (type: string) =>
      place.addressComponents?.find(c => c.types.includes(type));

    addressComponents = {
      houseNumber: findComponent('street_number')?.shortText,
      road: findComponent('route')?.longText,
      city: findComponent('locality')?.longText || findComponent('sublocality')?.longText,
      state: findComponent('administrative_area_level_1')?.shortText,
      postcode: findComponent('postal_code')?.shortText
    };
  } else {
    // Fall back to parsing formatted address
    addressComponents = parseFormattedAddress(place.formattedAddress);
  }

  // All POI results are treated as street_address level
  const featureType: AddressType = 'street_address';

  return {
    lat: place.location.latitude,
    lng: place.location.longitude,
    displayName: place.formattedAddress,
    businessName: place.displayName.text,
    type: featureType,
    importance: 1.0,
    addressComponents,
    phone: place.internationalPhoneNumber
  };
}

// =============================================================================
// API Configuration
// =============================================================================

const GOOGLE_PLACES_URL = 'https://places.googleapis.com/v1/places:searchText';
const FIELD_MASK = [
  'places.displayName',
  'places.formattedAddress',
  'places.location',
  'places.types',
  'places.internationalPhoneNumber',
  'places.addressComponents'
].join(',');

interface PlacesRequestBody {
  textQuery: string;
  maxResultCount: number;
  languageCode: string;
  locationBias?: {
    circle: { center: { latitude: number; longitude: number }; radius: number };
  };
}

/** Build request body for Google Places API */
function buildRequestBody(query: string, options: GooglePlacesOptions): PlacesRequestBody {
  const { limit = 5, proximity } = options;
  const body: PlacesRequestBody = {
    textQuery: query.trim(),
    maxResultCount: Math.min(limit, 10),
    languageCode: 'en'
  };

  if (proximity) {
    body.locationBias = {
      circle: {
        center: { latitude: proximity.lat, longitude: proximity.lng },
        radius: 50000
      }
    };
  }

  return body;
}

// =============================================================================
// Main Search Function
// =============================================================================

/**
 * Search for POI/businesses using Google Places API (New).
 *
 * Use this for location NAME searches (business names like "AutoZone", "Five Star Auto").
 * Google has more comprehensive POI coverage than Mapbox.
 */
export async function searchPOIWithGoogle(
  query: string,
  options: GooglePlacesOptions = {}
): Promise<GeocodedResult[]> {
  const apiKey = env.routing.googlePlacesApiKey;
  if (!apiKey) {
    console.error('[GooglePlaces] No API key configured');
    return [];
  }

  try {
    const response = await fetch(GOOGLE_PLACES_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Goog-Api-Key': apiKey,
        'X-Goog-FieldMask': FIELD_MASK
      },
      body: JSON.stringify(buildRequestBody(query, options))
    });

    if (!response.ok) {
      console.error('[GooglePlaces] API error:', response.status);
      return [];
    }

    const data: GooglePlacesResponse = await response.json();
    return data.places?.map(mapGooglePlaceToResult) || [];
  } catch (error) {
    console.error('[GooglePlaces] Fetch error:', error);
    return [];
  }
}
